import React, { useState, useEffect, useRef, useCallback } from "react";
import { resolveUrl } from "../../../utils/storeUtils";

function isCrossOriginUrl(url) {
  if (typeof url !== "string") return false;
  return url.startsWith("http://") || url.startsWith("https://");
}

function rgbToHex(r, g, b) {
  return (
    "#" +
    [r, g, b]
      .map((x) => {
        const h = Math.max(0, Math.min(255, Math.round(x))).toString(16);
        return h.length === 1 ? "0" + h : h;
      })
      .join("")
  );
}

/**
 * Modal that shows an image and lets the user hover/click to pick a pixel color.
 * Uses a canvas to avoid CORS tainting when reading pixel data.
 */
function PixelColorPicker({ imageUrl, onPickColor, onClose }) {
  const canvasRef = useRef(null);
  const [resolvedUrl, setResolvedUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [pixelColor, setPixelColor] = useState(null);
  const [cursorPos, setCursorPos] = useState(null);
  const imageRef = useRef(null);
  const blobUrlRef = useRef(null);
  const drawSizeRef = useRef({ w: 0, h: 0 });

  // Resolve cross-origin URLs to blob so canvas can read pixel data
  useEffect(() => {
    if (!imageUrl) {
      setResolvedUrl(null);
      setLoading(false);
      return;
    }
    const normalizedUrl = resolveUrl(String(imageUrl || ""));
    if (!isCrossOriginUrl(imageUrl)) {
      setResolvedUrl(normalizedUrl);
      setLoading(false);
      setLoadError(null);
      return;
    }
    setLoading(true);
    setLoadError(null);
    let cancelled = false;
    fetch(normalizedUrl, { mode: "cors" })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load image");
        return res.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
        const blobUrl = URL.createObjectURL(blob);
        blobUrlRef.current = blobUrl;
        setResolvedUrl(blobUrl);
        setLoading(false);
      })
      .catch((err) => {
        if (!cancelled) {
          setLoadError(err.message || "Failed to load image");
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [imageUrl]);

  const MAX_SIZE = 420;

  const drawImageToCanvas = useCallback(() => {
    const img = imageRef.current;
    const canvas = canvasRef.current;
    if (!img || !canvas || !img.complete) return;

    const natW = img.naturalWidth;
    const natH = img.naturalHeight;
    let w = natW;
    let h = natH;
    if (w > MAX_SIZE || h > MAX_SIZE) {
      const scale = Math.min(MAX_SIZE / w, MAX_SIZE / h);
      w = Math.round(w * scale);
      h = Math.round(h * scale);
    }
    canvas.width = w;
    canvas.height = h;
    drawSizeRef.current = { w, h };
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, w, h);
  }, []);

  useEffect(() => {
    if (!resolvedUrl) return;
    const img = new Image();
    img.crossOrigin = "anonymous";
    imageRef.current = img;
    img.onload = () => {
      drawImageToCanvas();
    };
    img.onerror = () => setLoadError("Failed to load image");
    img.src = resolvedUrl;
    return () => {
      imageRef.current = null;
    };
  }, [resolvedUrl, drawImageToCanvas]);

  const getPixelColor = useCallback((clientX, clientY) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = Math.floor((clientX - rect.left) * scaleX);
    const y = Math.floor((clientY - rect.top) * scaleY);
    if (x < 0 || x >= canvas.width || y < 0 || y >= canvas.height) return null;
    const ctx = canvas.getContext("2d");
    const [r, g, b] = ctx.getImageData(x, y, 1, 1).data;
    return rgbToHex(r, g, b);
  }, []);

  const handleMouseMove = (e) => {
    const hex = getPixelColor(e.clientX, e.clientY);
    setPixelColor(hex);
    setCursorPos({ x: e.clientX, y: e.clientY });
  };

  const handleMouseLeave = () => {
    setPixelColor(null);
    setCursorPos(null);
  };

  const handleClick = (e) => {
    const hex = getPixelColor(e.clientX, e.clientY);
    if (hex) onPickColor(hex);
  };

  return (
    <div className="pixel-color-picker-overlay" onClick={onClose}>
      <div className="pixel-color-picker-modal" onClick={(e) => e.stopPropagation()}>
        <h4>Pick color from image</h4>
        <p className="pixel-color-picker-hint">Move the cursor over the image, then click to save the pixel color.</p>
        <div className="pixel-color-picker-canvas-wrap">
          {loading && <p className="pixel-color-picker-loading">Loading image…</p>}
          {loadError && <p className="pixel-color-picker-error">{loadError}</p>}
          {resolvedUrl && !loadError && (
            <>
              <canvas
                ref={canvasRef}
                className="pixel-color-picker-canvas"
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
                onClick={handleClick}
                style={{ cursor: "crosshair" }}
              />
              {pixelColor && cursorPos && (
                <div
                  className="pixel-color-picker-tooltip"
                  style={{
                    left: cursorPos.x,
                    top: cursorPos.y,
                  }}
                >
                  <span className="pixel-color-picker-swatch" style={{ background: pixelColor }} />
                  <span className="pixel-color-picker-hex">{pixelColor}</span>
                </div>
              )}
            </>
          )}
        </div>
        <div className="pixel-color-picker-actions">
          <button type="button" className="pixel-color-picker-cancel" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default PixelColorPicker;
